import type { AcpMcpAppData } from '@crowdex/agent-contracts';
import {
  AppBridge,
  PostMessageTransport,
  RESOURCE_MIME_TYPE,
} from '@modelcontextprotocol/ext-apps/app-bridge';
import { CallToolResultSchema } from '@modelcontextprotocol/sdk/types.js';
import { AppWindow, LoaderCircle, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { sandboxedMcpAppHtml } from '@/features/mcp/apps/security';
import { errorMessage } from '@/lib/error-message';
import { isLocalMachine } from '@/lib/local-runtime';
import { nativePluginClient } from '@/lib/plugins/plugin-native-client';

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function normalizedToolResult(value: AcpMcpAppData['toolResult']) {
  const parsed = CallToolResultSchema.safeParse(value);
  if (parsed.success) return parsed.data;
  return {
    content: [
      { type: 'text' as const, text: typeof value === 'string' ? value : JSON.stringify(value) },
    ],
  };
}

export function McpAppFrame({
  data,
  resourceReader,
  toolCaller,
}: {
  data: AcpMcpAppData;
  resourceReader?: (uri: string) => Promise<{ uri: string; html: string }>;
  toolCaller?: (name: string, args: Record<string, unknown>) => Promise<Record<string, unknown>>;
}) {
  const bridgeRef = useRef<AppBridge | null>(null);
  const initializedBridgesRef = useRef(new WeakSet<AppBridge>());
  const [html, setHtml] = useState(data.html);
  const [height, setHeight] = useState(360);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(data.html === null);

  useEffect(() => {
    if (data.html !== null) return;
    if (!isLocalMachine()) {
      setError('This MCP App resource can only be loaded by Crowdex Desktop.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (resourceReader ?? nativePluginClient.readMcpResource)(data.resourceUri)
      .then((resource) => {
        if (!cancelled) setHtml(resource.html);
      })
      .catch((reason) => {
        if (!cancelled) setError(errorMessage(reason));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [data.html, data.resourceUri, resourceReader]);

  const closeBridge = useCallback((bridge: AppBridge) => {
    const close = () => void bridge.close().catch(() => undefined);
    if (!initializedBridgesRef.current.has(bridge)) {
      close();
      return;
    }
    void bridge
      .teardownResource({}, { timeout: 1_000 })
      .catch(() => undefined)
      .finally(close);
  }, []);

  const connectBridge = useCallback(
    (iframe: HTMLIFrameElement | null) => {
      const previous = bridgeRef.current;
      bridgeRef.current = null;
      if (previous !== null) closeBridge(previous);
      if (iframe?.contentWindow === null || iframe === null) return;

      const bridge = new AppBridge(
        null,
        { name: 'Crowdex', version: '3.0.0' },
        {
          openLinks: {},
          serverTools: {},
          serverResources: {},
          sandbox: {},
        },
        {
          hostContext: {
            theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
            displayMode: 'inline',
            availableDisplayModes: ['inline'],
            containerDimensions: { maxHeight: 720, maxWidth: 1200 },
            locale: navigator.language,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            platform: 'desktop',
            userAgent: navigator.userAgent,
            deviceCapabilities: {
              hover: matchMedia('(hover: hover)').matches,
              touch: navigator.maxTouchPoints > 0,
            },
          },
        },
      );
      bridgeRef.current = bridge;

      bridge.addEventListener('initialized', () => {
        initializedBridgesRef.current.add(bridge);
        void bridge
          .sendToolInput({ arguments: record(data.toolInput) })
          .then(() => bridge.sendToolResult(normalizedToolResult(data.toolResult)))
          .catch((reason) => setError(errorMessage(reason)));
      });
      bridge.addEventListener('sizechange', ({ height: requested }) => {
        if (requested !== undefined && Number.isFinite(requested)) {
          setHeight(Math.min(720, Math.max(160, requested)));
        }
      });
      bridge.oncalltool = async ({ name, arguments: args }) =>
        CallToolResultSchema.parse(
          await (toolCaller
            ? toolCaller(name, record(args))
            : nativePluginClient.callMcpTool(data.resourceUri, name, record(args))),
        );
      bridge.onreadresource = async ({ uri }) => {
        const resource = await (resourceReader ?? nativePluginClient.readMcpResource)(uri);
        return {
          contents: [{ uri: resource.uri, mimeType: RESOURCE_MIME_TYPE, text: resource.html }],
        };
      };
      bridge.onopenlink = async ({ url }) => {
        const allowed = /^https?:\/\//i.test(url);
        if (allowed) window.open(url, '_blank', 'noopener,noreferrer');
        return { isError: !allowed };
      };

      const transport = new PostMessageTransport(iframe.contentWindow, iframe.contentWindow);
      void bridge.connect(transport).catch((reason) => setError(errorMessage(reason)));
    },
    [closeBridge, data.resourceUri, data.toolInput, data.toolResult, resourceReader, toolCaller],
  );

  useEffect(
    () => () => {
      const bridge = bridgeRef.current;
      bridgeRef.current = null;
      if (bridge !== null) closeBridge(bridge);
    },
    [closeBridge],
  );

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center gap-2 rounded-xl border border-border bg-surface-panel text-muted-foreground text-sm">
        <LoaderCircle className="size-4 animate-spin" /> Loading MCP App…
      </div>
    );
  }
  if (error !== null || html === null) {
    return (
      <div className="rounded-xl border border-destructive/25 bg-destructive/5 p-4 text-sm">
        <p className="flex items-center gap-2 font-medium text-destructive">
          <TriangleAlert className="size-4" /> MCP App unavailable
        </p>
        <p className="mt-2 break-words text-muted-foreground text-xs leading-5">
          {error ?? `The host could not resolve ${data.resourceUri}.`}
        </p>
      </div>
    );
  }
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <header className="flex h-9 items-center gap-2 border-border border-b px-3 text-muted-foreground text-xs">
        <AppWindow className="size-3.5" />
        <span className="min-w-0 flex-1 truncate">MCP App · {data.resourceUri}</span>
        <span className="rounded bg-accent px-1.5 py-0.5 type-caption">Sandboxed</span>
      </header>
      <iframe
        className="block w-full border-0 bg-white"
        ref={connectBridge}
        sandbox="allow-forms allow-scripts"
        srcDoc={sandboxedMcpAppHtml(html)}
        style={{ height }}
        title={`MCP App ${data.resourceUri}`}
      />
    </section>
  );
}
